import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import withInspector from 'fulogics-ui-web/dist/layout/inspector/hoc/withInspector';
import Popup from 'fulogics-ui-web/dist/layout/dx/Popup/Popup';
import LabelStaticValue from 'fulogics-ui-web/dist/layout/components/label-static-value/LabelStaticValue';
import DataGrid from 'fulogics-ui-web/dist/layout/dx/DataGrid/DataGrid';
import useMemoDataSource from 'fulogics-ui-web/dist/layout/hooks/useMemoDataSource';
import { IdType } from 'fulogics-ui-core/dist/api/models/IdType';
import { DataSource } from 'devexpress-dashboard/model';
import { DataSourceOptions } from 'devextreme/data/data_source';
import { enTMSItemType, formatDate, keysJoin } from 'fulogics-ui-core';
import RouteLocationModelSimpleTex, {
    RouteLocationModelSimpleTextData,
    RouteLocationModelSimpleTextDataKeys,
} from 'fulogics-ui-web/dist/layout/components/route-location-model-simple-text/RouteLocationModelSimpleText';
import classNames from 'classnames';
import { iRootDispatch, iRootState } from '../../../../store/store';
import { COSOrderForwardStepPayloadStore } from '../../../../store/models/COSOrderForwardStepPayloadModel';
import * as ids from './inspectorOptions';
import { COSOrderReturnStepPayloadStore } from '../../../../store/models/COSOrderReturnStepPayloadModel';
import { Template } from 'devextreme-react/core/template';
import PayloadCarrierCountSimpleTextTemplate from '../../../templates/dataGrid/column/payload-carrier-count-simple-text-template/PayloadCarrierCountSimpleTextTemplate';
import AllowedStacksHeaderTemplate from '../../../templates/dataGrid/header/allowed-stacks-header-template/AllowedStacksHeaderTemplate';

const mapStateToProps = (state: iRootState) =>
    ({
        createForwardStepPayloadDataSource: (payloadCollectionId?: string) => {
            let opts = COSOrderForwardStepPayloadStore.createByCollectionId(payloadCollectionId);

            if (opts) opts.select = COSOrderForwardStepPayloadItemKeys;

            return opts;
        },
        createReturnStepPayloadDataSource: (payloadCollectionId?: string) => {
            let opts = COSOrderReturnStepPayloadStore.createByCollectionId(payloadCollectionId);

            if (opts) opts.select = COSOrderReturnStepPayloadItemKeys;

            return opts;
        },
    } as PayloadDetailsPopupDisplayOptions);

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: PayloadDetailsPopupDisplayOptions) =>
    ({
        ..._ownDispatch,
    } as PayloadDetailsPopupDisplayOptions);

const PayloadDetailsPopup = connect(
    mapStateToProps,
    mapDispatch as any
)(
    ({
        data,
        className,
        visible,
        isReturnStep,
        createForwardStepPayloadDataSource,
        createReturnStepPayloadDataSource,
        onHiding,
    }: PayloadDetailsPopupProps) => {
        const { t } = useTranslation('PayloadDetailsPopup');

        const returnStepPayloadDataSource = useMemoDataSource(
            () => createReturnStepPayloadDataSource?.(data?.payload?.id),
            [data?.payload?.id]
        );

        const forwardStepPayloadDataSource = useMemoDataSource(
            () => createForwardStepPayloadDataSource?.(data?.payload?.id),
            [data?.payload?.id]
        );

        return (
            <Popup visible={visible} inspectorId={ids.PayloadDetailsPopupId} onHiding={onHiding}>
                <div className={classNames(className)}>
                    <h4>{t('avigationPoint')}</h4>
                    <LabelStaticValue
                        label={t('avigationPoint')}
                        value={
                            <RouteLocationModelSimpleTex
                                inspectorId={ids.PayloadDetailsPopupAvigationPointId}
                                data={data?.location}
                            />
                        }
                    />
                    <LabelStaticValue
                        label={t('avigationDate')}
                        value={
                            data?.plannedStartDate
                                ? formatDate(data?.plannedStartDate, 'HH:mm / DD.MM.yyyy')
                                : '-'
                        }
                    />
                    <LabelStaticValue label={t('operationType')} value={t(`type.${data?.type}`)} />
                    <h4>{t('products')}</h4>
                    {isReturnStep ? (
                        <DataGrid
                            inspectorId={ids.PayloadDetailsPopupReturnStepPayloadGridId}
                            dataSource={returnStepPayloadDataSource}
                        >
                            <Template
                                name="AllowedStacksHeader"
                                render={AllowedStacksHeaderTemplate}
                            />
                        </DataGrid>
                    ) : (
                        <DataGrid
                            inspectorId={ids.PayloadDetailsPopupForwardStepPayloadGridId}
                            dataSource={forwardStepPayloadDataSource}
                        >
                            <Template
                                name="PayloadCarrierCountSimpleText"
                                render={PayloadCarrierCountSimpleTextTemplate}
                            />
                            <Template
                                name="AllowedStacksHeader"
                                render={AllowedStacksHeaderTemplate}
                            />
                        </DataGrid>
                    )}
                </div>
            </Popup>
        );
    }
);

export type PayloadDetailsPopupProps = {
    data?: PayloadDetailsPopupData;
} & PayloadDetailsPopupDisplayOptions;

export interface PayloadDetailsPopupDisplayOptions {
    className?: string;
    visible?: boolean;
    isReturnStep?: boolean;
    //
    createReturnStepPayloadDataSource?: (
        payloadCollectionId?: IdType
    ) => DataSource | DataSourceOptions | undefined;
    createForwardStepPayloadDataSource?: (
        payloadCollectionId?: IdType
    ) => DataSource | DataSourceOptions | undefined;
    onHiding?: () => void;
}

export type PayloadDetailsPopupData = {
    id?: IdType;
    type?: enTMSItemType;
    plannedStartDate?: Date | string;
    payload?: {
        id?: IdType;
    };
    location?: RouteLocationModelSimpleTextData;
};

export const PayloadDetailsPopupDataKeys = [
    'id',
    'type',
    'plannedStartDate',
    'payload.id',
    ...keysJoin(RouteLocationModelSimpleTextDataKeys, 'location.'),
];

export const COSOrderForwardStepPayloadItemKeys = [
    'id',
    'name',
    'sku',
    'productsQuantity',
    'carriersQuantity',
    'requestedAmount',
];

export const COSOrderReturnStepPayloadItemKeys = [
    'id',
    'product.name',
    'product.sku',
    'carrierType.name',
    'count',
];

export default PayloadDetailsPopup;
